import * as pdfjs from "pdfjs-dist/legacy/build/pdf.mjs";
import { createOcrWorker } from "./ocrWorker";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

export class PasswordRequiredError extends Error {
  constructor(incorrect) {
    super(incorrect ? "Incorrect password" : "Password required");
    this.incorrect = incorrect;
  }
}

function itemsToLines(items) {
  const rows = [];
  items.forEach((it) => {
    if (!it.str || !it.str.trim()) return;
    const y = it.transform[5];
    const x = it.transform[4];
    let row = rows.find((r) => Math.abs(r.y - y) < 3);
    if (!row) rows.push((row = { y, parts: [] }));
    row.parts.push({ x, s: it.str, w: it.width || 0 });
  });
  rows.sort((a, b) => b.y - a.y);
  return rows.map((r) => {
    r.parts.sort((a, b) => a.x - b.x);
    let out = "";
    let lastEnd = null;
    r.parts.forEach((p) => {
      if (lastEnd !== null && p.x - lastEnd > 1) out += " ";
      out += p.s;
      lastEnd = p.x + p.w;
    });
    return out;
  });
}

// Render the first pages of a PDF receipt as JPEG data URLs (for the accountant pack)
export async function pdfToImages(arrayBuffer, max = 3) {
  const doc = await pdfjs.getDocument({ data: new Uint8Array(arrayBuffer), isEvalSupported: false }).promise;
  const out = [];
  for (let i = 1; i <= Math.min(max, doc.numPages); i++) {
    const page = await doc.getPage(i);
    const viewport = page.getViewport({ scale: 1.6 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    out.push({ url: canvas.toDataURL("image/jpeg", 0.8), w: canvas.width, h: canvas.height });
  }
  return out;
}

export async function extractPdf(arrayBuffer, { password, onProgress, forceOcr } = {}) {
  let doc;
  try {
    doc = await pdfjs.getDocument({ data: new Uint8Array(arrayBuffer), password, isEvalSupported: false }).promise;
  } catch (e) {
    if (e?.name === "PasswordException") throw new PasswordRequiredError(e.code === 2);
    throw e;
  }
  const pages = [];
  let textChars = 0;
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const lines = itemsToLines(content.items);
    textChars += lines.join("").length;
    pages.push(lines.join("\n"));
    onProgress?.({ stage: "Reading text", page: i, total: doc.numPages });
  }
  const scanned = forceOcr || textChars < 40 * doc.numPages;
  if (!scanned) return { text: pages.join("\n"), pages: doc.numPages, ocr: false };

  const worker = await createOcrWorker();
  const ocrPages = [];
  for (let i = 1; i <= doc.numPages; i++) {
    onProgress?.({ stage: "Scanning image (OCR)", page: i, total: doc.numPages });
    const page = await doc.getPage(i);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    const { data } = await worker.recognize(canvas);
    ocrPages.push(data.text);
  }
  await worker.terminate();
  return { text: ocrPages.join("\n"), pages: doc.numPages, ocr: true };
}
